import yup from "../../../shared/validation/yupExtensions";
import { ListModel } from "shared/interfaces/core-interfaces";

//#region Planning Templates (CostCenterPlanning)

export interface PhaseNodeResponse {
    id: string;
    parentNodeId: string | null;
    name: string;
    description: string | null;
    sortOrder: number;
    depth: number;
    /** Set only on the node that was archived directly — the only node that can be unarchived. */
    isArchived: boolean;
    /** Set when this node or any node above it is archived: what to grey out and keep unselectable. */
    isArchivedEffective: boolean;
    archivedAtUnix: number | null;
    childNodes: PhaseNodeResponse[];
}

export interface PlanningTemplateResponse {
    id: string;
    name: string;
    description: string | null;
    // projectId: string | null;
    // project: string | null;
    // sourceTemplateId: string | null;
    phaseNodes: PhaseNodeResponse[];
}

export interface PlanningTemplateDTO {
    id: string;
    name: string;
    description: string | null;
}

export interface CreatePhaseNodeRequest {
    name: string;
    description?: string | null;
    childNodes?: CreatePhaseNodeRequest[];
}

export interface CreatePlanningTemplateRequest {
    name: string;
    description?: string | null;
    childNodes: CreatePhaseNodeRequest[];
}

export interface UpdatePlanningTemplateRequest {
    id: string;
    name: string;
    description?: string | null;
}

export interface AddPhaseNodeRequest {
    templateId: string;
    parentNodeId: string | null;
    name: string;
    description?: string | null;
    sortOrder: number;
}

export interface UpdatePhaseNodeRequest {
    id: string;
    parentNodeId: string | null;
    name: string;
    description?: string | null;
    sortOrder: number;
}

export interface SyncPhaseNodeRequest {
    id: string | null;
    name: string;
    description?: string | null;
    /** Has to be sent for every existing node: a sync unarchives whatever comes back as false. */
    isArchived: boolean;
    childNodes: SyncPhaseNodeRequest[];
}

export interface UpdatePlanningTreeRequest {
    templateId: string;
    childNodes: SyncPhaseNodeRequest[];
}

/** Base64-encoded phase CSV (columns-per-level layout) to parse into a phase-node tree. */
export interface ParsePlanningCsvRequest {
    fileBase64: string;
}

/** Local-only model for building nodes before saving.
 *  `serverId` is set for nodes seeded from an existing (server) tree so a staged edit can be
 *  committed as a full-tree sync (update in place); it is null/undefined for brand-new nodes. */
export interface LocalPhaseNode {
    localId: string;
    parentLocalId: string | null;
    serverId?: string | null;
    name: string;
    description: string | null;
    sortOrder: number;
    depth: number;
    /** Carried over from the server node a staged edit was seeded from, so a later sync
     *  doesn't unarchive it. Always false for brand-new nodes. */
    isArchived?: boolean;
    childNodes: LocalPhaseNode[];
}

export const PlanningTemplateScheme = yup.object().shape({
    name: yup.string().noLeadingSpace().required("Name is required."),
    description: yup.string().defined().optional(),
});

export const PhaseNodeScheme = yup.object().shape({
    name: yup.string().noLeadingSpace().required("Name is required."),
    description: yup.string().defined().optional(),
});

//#endregion

//#region Slice State

export interface TemplateSliceModel {
    planningTemplates: {
        listOfActivePlanningTemplates: PlanningTemplateDTO[];
        listOfInactivePlanningTemplates: PlanningTemplateDTO[];
        template: PlanningTemplateResponse | null;
    };
    additionalFieldsTemplates: {
        listOfActiveAdditionalFiledsTemplates: AdditionalInfoTemplateTableResponse[];
        listOfInactiveAdditionalFiledsTemplates: AdditionalInfoTemplateTableResponse[];
        createData: AdditionalInfoCreateData;
        template: AdditionalInfoTemplateResponse | null;
    };
}

export const initialValue: TemplateSliceModel = {
    planningTemplates: {
        listOfActivePlanningTemplates: [],
        listOfInactivePlanningTemplates: [],
        template: null,
    },
    additionalFieldsTemplates: {
        listOfActiveAdditionalFiledsTemplates: [],
        listOfInactiveAdditionalFiledsTemplates: [],
        createData: {
            dataTypes: [],
            templateTypes: []
        },
        template: null,
    }
};

//#endregion

//#region Additional Info Templates

export interface AdditionalInfoTemplateResponse {
    id?: string;
    name?: string,
    templateTypeId: number;
    isUsed: boolean,
    fields: ListWithListResponse[]
}

export interface AdditionalInfoTemplateTableResponse {
    id?: string;
    name?: string,
    templateType: string;
    isUsed: boolean,
    fields: ListWithListResponse[]
}

export interface ListWithListResponse {
    value: string;
    label: string;
    items: string[]
}

export interface AdditionalInfoTemplateRequest {
    id?: string;
    templateTypeId: number;
    name?: string,
    templateUDFs: AdditionalInfoUDFModel[]
}

export interface AdditionalInfoUDFModel {
    id?: string;
    title: string,
    type: number,
    options?: string[]
}

export interface AdditionalInfoCreateData {
    dataTypes: ListModel<number>[];
    templateTypes: ListModel<number>[];
}

export const AdditionalInfoTemplateRequestSchema = yup.object().shape({
    id: yup.string(),
    name: yup.string(),
    templateUDFs: yup.array<AdditionalInfoUDFModel>().required('Template items are required.').min(1, 'At least one item is required.'),
});

//#endregion